/**
 * Toasts.
 *
 * A store rather than context so a mutation callback anywhere — including code
 * that is not inside a component — can raise one without threading a prop.
 * Deliberately small: three stacked at most, auto-dismissing, no queueing, no
 * promise API. Anything that needs more attention than this belongs inline next
 * to the control the user touched.
 */

import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { create } from 'zustand'

import { cn } from '@/lib/cn'

import { IconButton } from './IconButton'

export type ToastVariant = 'info' | 'success' | 'error'

export interface ToastOptions {
  title: string
  description?: string
  variant?: ToastVariant
  /** Milliseconds before auto-dismiss. Pass 0 to keep it until dismissed. */
  duration?: number
}

export interface ToastRecord {
  id: string
  title: string
  description?: string
  variant: ToastVariant
}

const MAX_VISIBLE = 3
const DEFAULT_DURATION: Record<ToastVariant, number> = {
  info: 4000,
  success: 4000,
  error: 6500,
}

interface ToastStore {
  toasts: ToastRecord[]
  push: (toast: ToastRecord) => void
  remove: (id: string) => void
}

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (toast) =>
    set((state) => ({ toasts: [...state.toasts, toast].slice(-MAX_VISIBLE) })),
  remove: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),
}))

const timers = new Map<string, number>()
let nextId = 0

function clearTimer(id: string): void {
  const timer = timers.get(id)
  if (timer !== undefined) {
    window.clearTimeout(timer)
    timers.delete(id)
  }
}

export function dismissToast(id: string): void {
  clearTimer(id)
  useToastStore.getState().remove(id)
}

export function showToast(options: ToastOptions): string {
  const variant = options.variant ?? 'info'
  const id = `toast-${(nextId += 1)}`

  useToastStore.getState().push({
    id,
    title: options.title,
    description: options.description,
    variant,
  })

  const duration = options.duration ?? DEFAULT_DURATION[variant]
  if (duration > 0) {
    timers.set(id, window.setTimeout(() => dismissToast(id), duration))
  }
  return id
}

/**
 * Stable module-level functions, so this object never changes identity and is
 * safe in a dependency array.
 */
export const toast = {
  show: showToast,
  info: (title: string, description?: string) => showToast({ title, description, variant: 'info' }),
  success: (title: string, description?: string) =>
    showToast({ title, description, variant: 'success' }),
  error: (title: string, description?: string) =>
    showToast({ title, description, variant: 'error' }),
  dismiss: dismissToast,
} as const

export type ToastApi = typeof toast

export function useToast(): ToastApi {
  return toast
}

// --- rendering --------------------------------------------------------------

const VARIANT_ICON = {
  info: Info,
  success: CheckCircle2,
  error: TriangleAlert,
} as const

const VARIANT_ICON_COLOUR: Record<ToastVariant, string> = {
  info: 'text-text-muted',
  success: 'text-success',
  error: 'text-danger',
}

function ToastItem({ record }: { record: ToastRecord }) {
  const [entered, setEntered] = useState(false)
  const Icon = VARIANT_ICON[record.variant]

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(frame)
  }, [])

  return (
    <div
      className={cn(
        'pointer-events-auto flex w-full items-start gap-3 rounded-card border border-border bg-surface-raised p-3 shadow-xl',
        'transition-[opacity,translate] duration-200 ease-out',
        entered ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
      )}
    >
      <Icon
        className={cn('mt-0.5 h-5 w-5 shrink-0', VARIANT_ICON_COLOUR[record.variant])}
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-text">{record.title}</p>
        {record.description ? (
          <p className="mt-0.5 text-sm text-text-muted">{record.description}</p>
        ) : null}
      </div>
      <IconButton
        aria-label="Dismiss notification"
        size="sm"
        className="-mr-1 -mt-1"
        onClick={() => dismissToast(record.id)}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </IconButton>
    </div>
  )
}

/**
 * Anchored to the top so it never fights the docked player or the tab bar for
 * space at the bottom of the screen.
 */
export function Toaster() {
  const toasts = useToastStore((state) => state.toasts)

  return (
    <div
      role="region"
      aria-label="Notifications"
      aria-live="polite"
      // `fixed` ignores the body's safe-area padding, so on a notched iPhone
      // top-0 puts a toast behind the status bar. Offsetting by the inset lands
      // it just below, and z-[60] keeps it above the status bar scrim in
      // AppShell as well as every sticky toolbar.
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 p-3 pt-[calc(env(safe-area-inset-top)+0.75rem)] md:inset-x-auto md:right-4 md:top-4 md:w-88 md:items-end md:p-0 md:pt-0"
    >
      {toasts.map((record) => (
        <ToastItem key={record.id} record={record} />
      ))}
    </div>
  )
}
